import {StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import WorksheetStepStatusIcon from 'notebook/worksheets/steps/WorksheetStepStatusIcon';
import {useValidatedWorksheetContext} from 'notebook/worksheets/WorksheetContext';
import ProcedureStepGroup from 'procedures/shared-components/step-config/step-nav/ProcedureStepGroup';
import ProcedureStepsListItem from 'procedures/shared-components/step-config/step-nav/ProcedureStepsListItem';
import getTestProps from 'test/lib/cypress/getTestProps';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import {ElementMouseEvent} from 'util/Types';

import {WorksheetTaskStatusType} from '../WorksheetConstants';
import {optimisticStepStatusState} from './OptimisticStepStatusState';
import {extractStepTasksData} from './WorksheetUtils';

function StepStatusIcon({
  documentId,
  status,
}: {
  documentId: string;
  status: WorksheetTaskStatusType;
}): JSX.Element {
  const [optimisticStepStatus] = optimisticStepStatusState.useGlobalClientState(documentId);
  return (
    <WorksheetStepStatusIcon
      style={styles.icon}
      status={optimisticStepStatus ?? status}
      {...getTestProps({component: 'ProcedureStepsListItem', element: 'icon'})}
    />
  );
}

interface WorksheetStepSelectorProps {
  isWorksheetLocked: boolean;
  setSelectedStep: (stepGroupIndex?: number, stepIndex?: number) => void;
  selectedStep: {stepGroupIndex?: number; stepIndex?: number; documentId?: string};
  onAddStepClicked?: (e: ElementMouseEvent, stepGroupIndex: number, stepGroupName: string) => void;
  isAddStepDisabled?: boolean;
  addStepTooltip?: string;
  makeOverflowMenuItems?: (stepGroupIndex: number, stepIndex: number) => Array<OverflowMenuOption>;
  isWorksheetInReview?: boolean;
}

export default function WorksheetStepSelector({
  isWorksheetLocked,
  setSelectedStep,
  selectedStep,
  onAddStepClicked,
  isAddStepDisabled,
  addStepTooltip,
  makeOverflowMenuItems,
  isWorksheetInReview,
}: WorksheetStepSelectorProps): JSX.Element {
  const {
    worksheet: {worksheetStepGroups},
  } = useValidatedWorksheetContext();
  const onAddStepClickedCallback = useCallback(
    (stepGroupIndex: number, stepGroupName: string) => {
      if (!onAddStepClicked) {
        return undefined;
      }
      return (e: ElementMouseEvent) => {
        onAddStepClicked(e, stepGroupIndex, stepGroupName);
      };
    },
    [onAddStepClicked]
  );

  const setSelectedStepCallback = useCallback(
    (stepGroupIndex: number, stepIndex: number) => () => {
      setSelectedStep(stepGroupIndex, stepIndex);
    },
    [setSelectedStep]
  );
  return (
    <>
      {worksheetStepGroups.map((worksheetStepGroup, stepGroupIndex) => {
        const locked =
          isWorksheetLocked &&
          stepGroupIndex !== 0 &&
          stepGroupIndex !== worksheetStepGroups.length - 1 &&
          !isWorksheetInReview;
        const selectedIndex =
          selectedStep.stepGroupIndex === stepGroupIndex ? selectedStep.stepIndex : undefined;

        return (
          <ProcedureStepGroup
            key={`worksheet-step-group-${stepGroupIndex}`}
            name={worksheetStepGroup.name}
            showLockedIcon={locked}
            onAddStepClicked={onAddStepClickedCallback(stepGroupIndex, worksheetStepGroup.name)}
            isAddStepDisabled={isAddStepDisabled || Boolean(isWorksheetInReview)}
            addStepTooltip={isWorksheetInReview ? 'Adding steps is disabled' : addStepTooltip} // If in review, specific tooltip for disabled, otherwise whatever tooltip is provided through props, if any.
          >
            {worksheetStepGroup.worksheetSteps.map((step, stepIndex: number) => {
              const status = extractStepTasksData(step.tasks).status;
              return (
                <ProcedureStepsListItem
                  key={`worksheet-step-${stepIndex}`}
                  name={step.document.name}
                  isSelected={selectedIndex === stepIndex}
                  onClick={setSelectedStepCallback(stepGroupIndex, stepIndex)}
                  icon={
                    !locked && status ? (
                      <StepStatusIcon documentId={step.document.id} status={status} />
                    ) : null
                  }
                  overflowMenuItems={
                    !isWorksheetInReview && makeOverflowMenuItems
                      ? makeOverflowMenuItems(stepGroupIndex, stepIndex)
                      : undefined
                  }
                />
              );
            })}
          </ProcedureStepGroup>
        );
      })}
    </>
  );
}

const styles = StyleSheet.create({
  icon: {
    paddingLeft: '12px',
  },
});
